import React from "react";
import { Box } from "@mui/material";
import { Check, Loader2 } from "lucide-react";

// Design tokens — docs/design/design_handoff_jira_connect_1a/colors_and_type.css
// (scoped const, same idiom as IngestionCard.tsx's T)
export const T = {
    primary: "#94b2ff",
    primaryHover: "#1954ed",
    primarySoft: "rgba(148,178,255,0.07)",
    blue200: "#f7f9ff",
    blue700: "#103699",
    success: "#059669",
    error: "#dc2626",
    slate200: "#e2e8f0",
    slate300: "#cbd5e1",
    fg1: "#111827",
    fg2: "#374151",
    fg3: "#4b5563",
    fg4: "#6b7280",
    bgPaper: "#ffffff",
    bgInput: "#f3f3f5",
    bgSubtle: "#f1f5f9",
    border: "rgba(0,0,0,0.07)",
    borderStrong: "rgba(0,0,0,0.1)",
    divider: "rgba(0,0,0,0.07)",
    jiraBg: "#eff6ff",
    jiraBorder: "#bfdbfe",
    jiraGlyph: "#1d4ed8",
    bannerBg: "rgba(5,150,105,0.06)",
    bannerBorder: "#a7f3d0",
    bannerTile: "#ecfdf5",
    bannerTitle: "#065f46",
    bannerSub: "#047857"
};
export const SANS = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
export const MONO = "'JetBrains Mono', monospace";

// Motion per handoff §7 — CSS keyframes (framer-motion is not installed here).
// Exported so components that don't render CardShell (SyncCard's banner) can
// include them too — duplicate <style> tags with identical @keyframes are harmless.
export const KEYFRAMES = `
@keyframes jcRise { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
@keyframes jcSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
`;

export function CardShell({ active = false, children, testId }: {
    active?: boolean;
    children: React.ReactNode;
    testId?: string;
}) {
    return (
        <Box
            data-testid={testId}
            data-active={active ? "true" : "false"}
            sx={{
                fontFamily: SANS,
                background: T.bgPaper,
                border: active ? `1.5px solid ${T.blue200}` : `1px solid ${T.border}`,
                boxShadow: active ? `0 0 0 3px ${T.primarySoft}` : "none",
                borderRadius: "12px",
                overflow: "hidden",
                animation: "jcRise 0.22s cubic-bezier(0.22,0.61,0.36,1)"
            }}
        >
            <style>{KEYFRAMES}</style>
            {children}
        </Box>
    );
}

export function ToolBadge({ name }: { name: string }) {
    return (
        <Box component="span" sx={{
            fontSize: "11px", fontWeight: 600, color: T.blue700, background: T.primarySoft,
            border: `1px solid ${T.blue200}`, borderRadius: "999px", padding: "2px 9px",
            fontFamily: MONO, whiteSpace: "nowrap"
        }}>
            {name}
        </Box>
    );
}

export function StatusChip({ tone, label }: { tone: "success" | "neutral" | "error"; label: string }) {
    const color = tone === "success" ? T.success : tone === "error" ? T.error : T.fg4;
    return (
        <Box component="span" sx={{
            marginLeft: "auto", display: "flex", alignItems: "center", gap: "5px",
            fontSize: "11px", fontWeight: tone === "neutral" ? 600 : 700, color, flex: "0 0 auto"
        }}>
            {tone === "success" && <Check size={13} strokeWidth={2.6} />}
            {label}
        </Box>
    );
}

export function JiraTile({ size = 34, label = "J" }: { size?: number; label?: string }) {
    return (
        <Box component="span" sx={{
            width: size, height: size, borderRadius: size >= 30 ? "8px" : "6px",
            background: T.jiraBg, border: `1px solid ${T.jiraBorder}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: T.jiraGlyph, fontWeight: 700, fontSize: `${Math.round(size * 0.44)}px`, flex: "0 0 auto"
        }}>
            {label}
        </Box>
    );
}

export function CardHeader({ tool, right, divider = true }: {
    tool: string;
    right?: React.ReactNode;
    divider?: boolean;
}) {
    return (
        <Box sx={{
            display: "flex", alignItems: "center", gap: "10px", padding: "11px 14px",
            borderBottom: divider ? `1px solid ${T.divider}` : "none"
        }}>
            <ToolBadge name={tool} />
            {right}
        </Box>
    );
}

const buttonBase = {
    height: "36px", borderRadius: "9px", fontSize: "12.5px", fontWeight: 500,
    fontFamily: SANS, cursor: "pointer", border: "none",
    "&:disabled": { opacity: 0.55, cursor: "default" }
} as const;

export function PrimaryButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
    return (
        <Box component="button" type="button" onClick={onClick} disabled={disabled}
            sx={{ ...buttonBase, padding: "0 15px", background: T.primary, color: "#fff" }}>
            {label}
        </Box>
    );
}

export function OutlineButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
    return (
        <Box component="button" type="button" onClick={onClick} disabled={disabled}
            sx={{ ...buttonBase, padding: "0 13px", background: T.bgPaper, color: T.fg2, border: `1px solid ${T.borderStrong}` }}>
            {label}
        </Box>
    );
}

export function Spinner({ size = 16 }: { size?: number }) {
    return (
        <Box component="span" sx={{ display: "inline-flex", animation: "jcSpin 1s linear infinite", color: T.primary }}>
            <Loader2 size={size} strokeWidth={1.9} />
        </Box>
    );
}
